// ---------------------------------------------------------------------------
// Service → Technology (USES_TECH)
// ---------------------------------------------------------------------------
//
// Nested under a parent `serviceId` with a composite `(serviceId, technologyId)`
// key, so this does not fit the flat single-id `createEntityCrudClient` shape.

import { apiRequest } from './http';

/**
 * Input for assigning a technology to a service
 */
export interface AssignTechnologyInput {
  /** Technology entity ID (tech-*) */
  technologyId: string;
  /** Optional notes about how the technology is used */
  notes?: string;
  /**
   * Optional published `TechnologyVersion` id to pin (issue #1284). Must
   * belong to `technologyId` and be `status: published` — the server
   * rejects a mismatched/unpublished value with 409 CONFLICT rather than
   * silently dropping it.
   */
  versionId?: string;
}

/**
 * Metadata shape returned on a USES_TECH edge (issue #1284).
 */
export interface UsesTechEdgeMetadata {
  notes?: string;
  role?: string;
  versionId?: string;
}

/**
 * POST /api/v1/services/:serviceId/technologies
 * Assign a technology to a service.
 */
export async function assignTechnologyToService(
  serviceId: string,
  input: AssignTechnologyInput
): Promise<{ fromEntityId: string; toEntityId: string; metadata: UsesTechEdgeMetadata }> {
  return apiRequest(`/services/${serviceId}/technologies`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Partial update input for an existing technology assignment (issue #1284).
 * `versionId: null` explicitly clears a previously pinned version;
 * `versionId: undefined` (omitted) leaves it untouched.
 */
export interface UpdateTechnologyAssignmentInput {
  notes?: string;
  versionId?: string | null;
}

/**
 * PATCH /api/v1/services/:serviceId/technologies/:technologyId
 * Update an existing technology assignment's `notes`/`versionId` in place,
 * without delete+recreate — see `backend/src/routes/serviceTechnologies.ts`
 * for the update-path decision (issue #1284).
 */
export async function updateServiceTechnology(
  serviceId: string,
  technologyId: string,
  input: UpdateTechnologyAssignmentInput
): Promise<{ fromEntityId: string; toEntityId: string; metadata: UsesTechEdgeMetadata }> {
  return apiRequest(`/services/${serviceId}/technologies/${technologyId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/**
 * DELETE /api/v1/services/:serviceId/technologies/:technologyId
 * Remove a technology assignment from a service.
 */
export async function removeTechnologyFromService(
  serviceId: string,
  technologyId: string
): Promise<void> {
  return apiRequest<void>(`/services/${serviceId}/technologies/${technologyId}`, {
    method: 'DELETE',
  });
}
